import { DateTime } from 'luxon';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';
import {
  alphanumericRegex,
  // fiscalCodeRegex,
  nameSurnameRegex,
  portionRegex,
  regexOnlyNumber,
  regVolRegex,
  seriesRegex,
} from 'shell/constants/regex';
import { CommonInterfaceSubscription } from 'subscription/view-models/common-interface-subscription';
import * as yup from 'yup';

const validationSchemaForeignResidence = yup
  .object()
  .shape({
    addressForeign: yup.object().shape({
      address: yup.string().required('Campo obbligatorio'),
    }),
    // subscriptionBirthData: yup.object().shape({
    //   familyAttribute: yup.string().required('Campo obbligatorio'),
    // }),
    editDataControl: yup.object().shape({
      dateValidity: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .required('Campo obbligatorio')
        .max(
          DateTime.local().plus({ days: 1 }),
          'La data non può essere successiva alla data odierna'
        ),
      // assume: yup.string().required('Campo obbligatorio'),
      // requested: yup.string().required('Campo obbligatorio'),
      // subscribeTo: yup.string().required('Campo obbligatorio'),
      flag: yup.string().required('Campo obbligatorio'),
    }),
    generality: yup.object().shape({
      fiscalCode: yup.string().nullable().notRequired(),
      // .matches(fiscalCodeRegex, 'Formato non valido'),
      surname: yup
        .string()
        .when('withoutSurname', {
          is: false,
          then: yup.string().required('Campo obbligatorio'),
          otherwise: yup.string(),
        })
        .matches(
          nameSurnameRegex,
          'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
        ),
      withoutSurname: yup.boolean(),
      name: yup
        .string()
        .when('withoutName', {
          is: false,
          then: yup.string().required('Campo obbligatorio'),
          otherwise: yup.string(),
        })
        .matches(
          nameSurnameRegex,
          'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
        ),
      withoutName: yup.boolean(),
      gender: yup.string().required('Campo obbligatorio'),
      aire: yup.string().notRequired(),
      birthDate: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .required('Campo obbligatorio')
        .max(DateTime.now(), 'La data deve essere minore della data odierna'),
      // generality italian form
      italian: yup
        .object()
        .nullable()
        .when('isForeign', {
          is: (val: boolean) => val === false,
          then: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({
                  description: yup.string().required('Campo obbligatorio'),
                })
                .required('Campo obbligatorio'),
              // province: yup.string().required('Campo obbligatorio'),
            }),
          otherwise: yup
            .object()
            .nullable()
            .shape({
              municipality: yup.object().nullable().shape({
                description: yup.string().notRequired().nullable(),
              }),
              province: yup.string().notRequired().nullable(),
            })
            .notRequired(),
        }),
      foreign: yup
        .object()
        .nullable()
        .when('isForeign', {
          is: (val: boolean) => val === true,
          then: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({
                  description: yup.string().required('Campo obbligatorio'),
                })
                .required('Campo obbligatorio'),
              state: yup
                .object()
                .nullable()
                .shape({
                  value: yup.string().required('Campo obbligatorio'),
                })
                .required('Campo obbligatorio'),
            }),
          otherwise: yup
            .object()
            .nullable()
            .shape({
              municipality: yup.object().nullable().shape({
                description: yup.string().notRequired().nullable(),
              }),
              state: yup.object().nullable().shape({
                value: yup.string().notRequired().nullable(),
              }),
            }),
        }),
      // Other fields generality
      exceptionalBirthPlace: yup.string(),
      municipalityIdentifier: yup.string(),
      istatCode: yup.string().notRequired(),
      // anprIdentifier: yup.string().required('Campo obbligatorio'),
      isForeign: yup.boolean(),
    }),
    birthdayDocument: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).nullable().required('Campo obbligatorio'),
        // istatCode?: string;
        // province?: string;
        // location: yup.string().required('Campo obbligatorio'),
        townHallOffice: yup.string().notRequired(),
        year: yup
          .string()
          .matches(regexOnlyNumber, 'Il campo deve essere numerico')
          .required('Campo obbligatorio'),
        portion: yup
          .string()
          .required('Campo obbligatorio')
          .matches(portionRegex, 'Il campo deve avere valore 1 o 2'),
        serie: yup
          .string()
          .nullable()
          .notRequired()
          .matches(seriesRegex, 'Il campo deve essere A, B, C, C1, C2 o C3'),
        documentNumber: yup
          .string()
          .matches(regexOnlyNumber, 'Il campo deve essere numerico')
          .required('Campo obbligatorio'),
        volume: yup
          .string()
          .nullable()
          .when('municipality', (value: MunicipalityAnprVM) => {
            if (value?.id === ISTAT_MILANO) {
              return yup
                .string()
                .required('Campo obbligatorio')
                .matches(regVolRegex, 'Formato non valido, primo carettere deve essere: R');
            }
            return yup.string().nullable();
          }),
        documentDate: yup
          .date()
          .nullable()
          // .required('Campo obbligatorio')
          .typeError('Data non valida'),
        transcript: yup.boolean(),
      })
      .test('Check birthday document', '', (values) => {
        const municipalityAnpr = values?.municipality as MunicipalityAnprVM;
        if (municipalityAnpr?.id === ISTAT_MILANO && !values?.volume) {
          return new yup.ValidationError(
            'Il campo è obbligatorio',
            undefined,
            'birthdayDocument.volume'
          );
        }
        return true;
      }),
    citizenship: yup.object().shape({
      state: yup.object().shape({}).nullable().required('Campo obbligatorio'),
      note: yup.string(),
      validity: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
    }),
    fatherhood: yup
      .object()
      .default(null)
      .nullable()
      .shape({
        generality: yup.object().shape({
          surname: yup
            .string()
            .when('withoutSurname', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutSurname: yup.boolean(),
          name: yup
            .string()
            .when('withoutName', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutName: yup.boolean(),
          gender: yup.string(),
          aire: yup.string(),
          birthDate: yup.date().nullable().typeError('Data non valida'),
          // Other fields generality
          exceptionalBirthPlace: yup
            .string()
            .notRequired()
            .nullable()
            .matches(alphanumericRegex, 'Il campo non è valido'),
          municipalityIdentifier: yup.string().notRequired().nullable(),
          // istatCode: yup.string().required('Campo obbligatorio'),
          // anprIdentifier: yup.string().required('Campo obbligatorio'),
          isForeign: yup.boolean(),
        }),
      }),
    motherhood: yup
      .object()
      .default(null)
      .nullable()
      .shape({
        generality: yup.object().shape({
          surname: yup
            .string()
            .when('withoutSurname', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutSurname: yup.boolean(),
          name: yup
            .string()
            .when('withoutName', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutName: yup.boolean(),
          gender: yup.string(),
          aire: yup.string(),
          birthDate: yup.date().nullable().typeError('Data non valida'),
          // Other fields generality
          exceptionalBirthPlace: yup.string(),
          municipalityIdentifier: yup.string(),
          // istatCode: yup.string().required('Campo obbligatorio'),
          // anprIdentifier: yup.string().required('Campo obbligatorio'),
          isForeign: yup.boolean(),
        }),
      }),
  })
  // .test(
  //   'birthdayDocument.documentDate',
  //   'La data non può essere minore della Data di nascita',
  //   (values) => {
  //     const castedValues = values as unknown as CommonInterfaceSubscription;

  //     if (
  //       castedValues.generality?.birthDate &&
  //       castedValues.birthdayDocument?.documentDate &&
  //       castedValues.generality?.birthDate > castedValues.birthdayDocument?.documentDate
  //     ) {
  //       return new yup.ValidationError(
  //         'La data non può essere minore della Data di nascita',
  //         undefined,
  //         'birthdayDocument.documentDate'
  //       );
  //     }

  //     return false;
  //   }
  // )
  .test('citizenship.validity', 'La data deve essere uguale alla Data di nascita', (values) => {
    const castedValues = values as unknown as CommonInterfaceSubscription;

    if (
      castedValues.generality?.birthDate &&
      castedValues.citizenship?.validity &&
      castedValues.generality?.birthDate > castedValues.citizenship?.validity
    ) {
      return new yup.ValidationError(
        'La data deve essere uguale o maggiore alla Data di nascita',
        undefined,
        'citizenship.validity'
      );
    }

    const today = DateTime.now();

    if (castedValues.citizenship?.validity && castedValues.citizenship?.validity > today) {
      return new yup.ValidationError(
        'La data deve essere minore uguale alla data di oggi',
        undefined,
        'citizenship.validity'
      );
    }

    return false;
  })
  // .test('birthdayDocument.year', "L'anno non può essere minore della Data di nascita", (values) => {
  //   const castedValues = values as unknown as CommonInterfaceSubscription;

  //   const birthYear = castedValues.generality?.birthDate
  //     ? castedValues.generality?.birthDate.year
  //     : undefined;

  //   if (
  //     birthYear &&
  //     castedValues.birthdayDocument?.year &&
  //     birthYear > Number.parseInt(castedValues.birthdayDocument?.year, 10)
  //   ) {
  //     return new yup.ValidationError(
  //       "L'anno non può essere minore della Data di nascita",
  //       undefined,
  //       'birthdayDocument.year'
  //     );
  //   }

  //   return false;
  // })
  .test('Check all shape', '', (values) => {
    if (values.editDataControl.flag !== '1' && !values.generality.fiscalCode) {
      return new yup.ValidationError('Il campo è obbligatorio', undefined, 'generality.fiscalCode');
    }

    return false;
  });

export default validationSchemaForeignResidence;
